/**
 * 라이브 Pod 로그 계약 (ADR 0021 · Issue #9)
 * --------------------------------------------------------------------------
 * Quickwit Logs Explorer(ADR 0003/0006)를 대체하지 않습니다. kubelet의 현재
 * stdout/stderr을 짧게 들여다보는 **진단용 stream**이고, 장기 검색·facet·상관분석은
 * 그대로 Logs Explorer가 맡습니다.
 *
 * 규칙
 * - 원문은 브라우저로 내려가지 않습니다. 서버 masker를 통과한 `maskedLine`만 실립니다. (ADR 0003)
 * - Pod 신원은 이름이 아니라 UID입니다. 같은 이름의 재생성 Pod는 409 `pod_replaced`입니다.
 * - 응답은 NDJSON입니다. 한 줄 = 프레임 하나이고, 마지막 프레임은 항상 control입니다.
 */

/** container 종류. Kubernetes는 init/normal을 통틀어 이름이 유일하므로 선택 키는 이름 하나입니다. */
export type LiveLogContainerKind = "container" | "init";

/** informer 스냅숏이 본 container 실행 상태. */
export type LiveLogContainerState = "Running" | "Waiting" | "Terminated" | "Unknown";

/** 한 container의 라이브 로그 선택지. */
export interface LiveLogContainer {
  name: string;
  kind: LiveLogContainerKind;
  state: LiveLogContainerState;
  restarts: number;
  /**
   * 재시작 이력이 있어 `previous=true` 조회가 성공할 **가능성**이 있는지.
   * kubelet이 이전 로그를 이미 회수했을 수 있으므로 보장이 아닙니다 —
   * 없으면 404 `previous_unavailable`로 정직하게 끝납니다.
   */
  previousAvailable: boolean;
}

/** 서버가 강제하는 상한. UI는 고르지 않고 표시만 합니다. */
export interface LiveLogLimits {
  tailLinesDefault: number;
  tailLinesMax: number;
  maxSinceSeconds: number;
  /** 논리적 한 줄의 바이트 상한. 넘으면 잘리고 `truncated: true`가 붙습니다. */
  maxLineBytes: number;
  /** 세션 하나의 벽시계 상한(초). */
  sessionSeconds: number;
  /** 세션 하나가 내려보낼 수 있는 총 바이트. */
  maxResponseBytes: number;
  maxStreamsPerSubject: number;
  maxStreamsPerCluster: number;
}

/**
 * `GET /api/v1/clusters/{clusterId}/pods/{name}/live-logs/containers`
 *
 * informer 스냅숏에서만 만듭니다 — 이 경로는 Kubernetes API를 호출하지 않습니다. (ADR 0004)
 */
export interface LiveLogContainerCatalog {
  clusterId: string;
  namespace: string;
  podName: string;
  podUid: string;
  phase: string;
  /** deletionTimestamp가 있는 Pod. 스냅숏은 되지만 follow는 곧 끝납니다. */
  terminating: boolean;
  containers: LiveLogContainer[];
  limits: LiveLogLimits;
  generatedAt: string;
}

/**
 * 프레임의 stream 라벨입니다.
 *
 * kubelet의 pods/log는 stdout과 stderr을 **하나로 합쳐** 내보내고 어느 쪽인지
 * 표시하지 않습니다. 없는 구분을 지어내지 않고 `combined` 하나만 둡니다.
 */
export type LiveLogStream = "combined";

/** 마스킹된 로그 한 줄. */
export interface LiveLogLineFrame {
  type: "line";
  /** 세션 안에서 1부터 증가합니다. 재접속하면 다시 1부터입니다. */
  seq: number;
  /** `timestamps=true`일 때만 있습니다. kubelet이 붙인 RFC3339 값입니다. */
  timestamp?: string;
  stream: LiveLogStream;
  /** 서버 masker를 통과한 본문. 원문 조각은 남아 있지 않습니다. */
  maskedLine: string;
  /** 이 줄에 가려진 구간이 있었는지. 원문 위치·길이는 싣지 않습니다. */
  masked: boolean;
  /** maxLineBytes에서 잘렸는지. */
  truncated: boolean;
}

/** 세션이 끝난 이유. 닫힌 집합이며 metric 라벨과 같은 값입니다. */
export type LiveLogDisconnectReason =
  | "completed"
  | "session_timeout"
  | "response_bytes_exceeded"
  | "backpressure"
  | "client_canceled"
  | "upstream_error"
  | "upstream_timeout"
  | "masker_failed"
  | "server_shutdown";

/**
 * 재접속 힌트. **이미 마스킹된 프레임에서만** 유도한 유계 메타데이터입니다.
 * 로그 본문·커서·내부 주소·질의는 담지 않습니다.
 *
 * `sinceTime`을 그대로 `LiveLogOptions.sinceTime`으로 되돌려 보내면 끊긴 지점부터
 * 이어집니다. 경계가 포함이라 마지막 줄 하나가 겹칠 수 있습니다.
 */
export interface LiveLogResumeHint {
  /** 마지막으로 내려보낸 줄의 timestamp. `timestamps=true`였을 때만 있습니다. */
  sinceTime?: string;
  /** 이 세션에서 내려보낸 줄 수. */
  deliveredLines: number;
  /** 다시 붙을 때 권장하는 sinceSeconds. sinceTime이 없을 때의 대안입니다. */
  suggestedSinceSeconds?: number;
}

/**
 * 세션의 종료 프레임입니다.
 *
 * **최선 노력입니다.** 연결이 이미 끊겼거나, 쓰기가 막혔거나, 전송 바이트 상한에
 * 정확히 닿았으면 이 프레임은 도달하지 않습니다. 종료 사유의 권위 있는 출처는
 * 서버 metric(`dashboard_live_logs_disconnects_total`)이고, 이 프레임은 화면이
 * 사유를 바로 보여주기 위한 편의입니다.
 */
export interface LiveLogControlFrame {
  type: "control";
  reason: LiveLogDisconnectReason;
  /** 사용자에게 보여줄 짧은 문구. 내부 주소·질의·스택트레이스를 담지 않습니다. */
  message: string;
  resume?: LiveLogResumeHint;
}

export type LiveLogFrame = LiveLogLineFrame | LiveLogControlFrame;

/** 브라우저가 보내는 조회 옵션. 서버가 상한을 강제하므로 값은 힌트가 아니라 요청입니다. */
export interface LiveLogOptions {
  container: string;
  tailLines?: number;
  sinceSeconds?: number;
  /**
   * 이어보기 지점(RFC3339). `LiveLogResumeHint.sinceTime`을 그대로 되돌려 보냅니다.
   * `sinceSeconds`와 함께 쓸 수 없고, 최근 24시간 안이어야 합니다.
   *
   * kubelet의 경계는 **포함(inclusive)** 이라 이 시각의 줄이 다시 올 수 있습니다.
   * 화면은 중복 가능성을 감추지 않고 그대로 알립니다.
   */
  sinceTime?: string;
  timestamps?: boolean;
  /** 이전 인스턴스(crash) 로그. `follow`와 함께 쓸 수 없습니다. */
  previous?: boolean;
  follow?: boolean;
}

/** 라이브 로그 경로가 돌려주는 `APIError.code`의 닫힌 집합입니다. */
export type LiveLogErrorCode =
  | "live_logs_disabled"
  | "invalid_options"
  | "pod_uid_required"
  | "forbidden"
  | "pod_not_found"
  | "pod_replaced"
  | "container_not_found"
  | "container_not_started"
  | "previous_unavailable"
  | "live_logs_quota"
  | "upstream_timeout"
  | "upstream_error";

/**
 * 사용자 문구는 **서버가 소유합니다.**
 *
 * control 프레임의 `message`와 `APIError.message`에 이미 짧은 한국어 문구가 실려
 * 옵니다. 같은 문구를 브라우저 번들에 한 번 더 싣지 않습니다 — 두 곳이 어긋나면
 * 사용자가 보는 문구가 서버가 아는 사실과 달라지고, 화면은 서버가 모르는 이유를
 * 지어내게 됩니다. 코드(`LiveLogErrorCode`/`LiveLogDisconnectReason`)는 분기용이고,
 * 표시할 문장은 응답에서 그대로 씁니다.
 */

/** NDJSON 본문의 Content-Type. 서버·브라우저·OpenAPI가 같은 값을 씁니다. */
export const LIVE_LOG_CONTENT_TYPE = "application/x-ndjson";
